/**
 * balanceChartData — shape the daily series for the FIXED chart window
 * [today−12mo … today+12mo]. The window never moves with the selected month;
 * empty days are null-padded so the categorical axis keeps fixed edges and
 * even spacing.
 */
import type { DailyBalancePoint } from './types';
import { addDays, firstDayOfMonth, lastDayOfMonth } from './computeBalanceSeries';
import { chartWindowRange, windowFirstYm, windowLastYm } from './sharedChartWindow';

export interface ChartPoint {
  date: string;
  balanceEur: number | null;
  isActual: boolean;
}

/** A ChartPoint split into the solid-Ist (``actualEur``) and dashed-Plan
 *  (``planEur``) series so the curve renders as two recharts ``<Line>``s. */
export interface SplitChartPoint extends ChartPoint {
  actualEur: number | null;
  planEur: number | null;
}

/** Slice + null-pad ``dailySeries`` to the fixed window around ``todayYm``. */
export function fixedChartWindow(
  dailySeries: DailyBalancePoint[],
  todayYm: string,
): ChartPoint[] {
  // #2955 §1 — the span comes from ``sharedChartWindow`` rather than being
  // spelled out again here: the columns are built over the SAME span now, and
  // the day↔month bridge between them is only exact while the two agree. Two
  // copies of "±12 months" is exactly how they would drift apart.
  const { startDate: windowStart, endDate: windowEnd } = chartWindowRange(todayYm);

  const byDate = new Map<string, DailyBalancePoint>();
  for (const p of dailySeries) {
    if (p.date >= windowStart && p.date <= windowEnd) byDate.set(p.date, p);
  }

  const out: ChartPoint[] = [];
  for (let d = windowStart; d <= windowEnd; d = addDays(d, 1)) {
    const p = byDate.get(d);
    out.push(
      p
        ? { date: d, balanceEur: p.balanceEur, isActual: p.isActual }
        : { date: d, balanceEur: null, isActual: false },
    );
  }
  return out;
}

/**
 * Split the spine into a solid-Ist line (``actualEur``, history ≤ today) and a
 * dashed-Plan line (``planEur``, projection > today), per #1414's "solid up to
 * today, dashed after". The LAST actual day carries BOTH values so the two
 * recharts ``<Line>``s share that vertex and join with no visible gap.
 */
export function splitActualPlan(points: ChartPoint[]): SplitChartPoint[] {
  // Index of the last actual point that has data = the bridge vertex.
  let bridge = -1;
  for (let i = 0; i < points.length; i++) {
    if (points[i].isActual && points[i].balanceEur != null) bridge = i;
  }
  return points.map((p, i) => ({
    ...p,
    actualEur: p.isActual ? p.balanceEur : null,
    // Plan line covers the projection AND the bridge vertex (so it connects).
    planEur: !p.isActual ? p.balanceEur : i === bridge ? p.balanceEur : null,
  }));
}

/** Contiguous x-ranges where the curve dips below the KK-limit, so the chart
 *  can shade the breach windows (connecting the banner warning to the curve).
 *  A null balance (padded day) or a value at/above the limit ends a run. No
 *  limit → no segments. */
export function breachSegments(
  points: ChartPoint[],
  kkLimitEur: number | null,
): { x1: string; x2: string }[] {
  if (kkLimitEur == null) return [];
  const segments: { x1: string; x2: string }[] = [];
  let start: string | null = null;
  let last: string | null = null;
  for (const p of points) {
    if (p.balanceEur != null && p.balanceEur < kkLimitEur) {
      if (start == null) start = p.date;
      last = p.date;
    } else if (start != null) {
      segments.push({ x1: start, x2: last! });
      start = null;
      last = null;
    }
  }
  if (start != null) segments.push({ x1: start, x2: last! });
  return segments;
}

/** The subset of a chart row the y-floor cares about: the two plottable series
 *  values — the shared history and the single plan line. */
export interface ChartFloorRow {
  actualEur: number | null;
  planEur: number | null;
}

/**
 * The room the y-floor guarantees BELOW the KK-limit (HOF-16), as a fraction of
 * the plotted span above it.
 *
 * Proportional rather than a euro amount, because it is a PIXEL problem: where
 * the clearance binds, the rendered domain is ``[limit − pad, dataMax]``, so the
 * strip below the rule is ``f / (1 + f)`` of the plot height whatever the farm's
 * magnitudes — 9,1 % of the ~242 px plot (280 px less the 8 px top margin and
 * the x-axis) ≈ 22 px, for an 11 px caption recharts hangs below the rule. A
 * euro pad would be invisible on a Mio.-€ chart and swallow a small one.
 */
const KK_LIMIT_FLOOR_CLEARANCE = 0.1;

/**
 * The chart's lower y-bound (#1509) — the single source of truth shared by the
 * ``<YAxis domain>`` and the breach ``ReferenceArea`` ``y2``, replacing the old
 * hardcoded −1.000.000 floor that clipped deep breaches.
 *
 * It is the minimum over the series ACTUALLY plotted: the history
 * (``actualEur``) plus the projection (``planEur``). #2684 retired the
 * weather/economy scenarios, so there is exactly one plan line and the floor no
 * longer depends on a selection.
 *
 * The KK-limit anchors the floor (``min(dataMin, kkLimit)``) so a breach band
 * always has vertical room even when the data barely dips, and a non-breaching
 * chart still gets a sensible (never inverted) domain. With no plotted value the
 * floor falls back to the limit (or 0 when the limit is unset).
 *
 * HOF-16 — that anchor used to land EXACTLY on the floor. Whenever the balance
 * stayed above the limit (the healthy case, and nearly every tenant nearly all
 * the time) ``min`` returned the limit untouched, so the dashed rule was drawn
 * on the axis and its ``insideTopRight`` caption, which recharts hangs BELOW the
 * line, landed in the x-tick strip. The rule was legible only on a farm in
 * trouble — backwards, on the screen whose job is conveying financial calm.
 *
 * So the floor now GUARANTEES ``KK_LIMIT_FLOOR_CLEARANCE`` of room under the
 * limit. Note what the condition is not: the first cut of this fix keyed on
 * "nothing breaches" and left the breaching branch alone, which reads right and
 * is wrong — a balance dipping 1 € under a −50.000 € limit breaches, and puts
 * the trough (and so the axis) 1 € under the rule, ≈0,002 px apart. The caption
 * lands back in the tick strip on the very chart announcing the breach. The
 * defect was never "does it breach"; it is "is the rule a hair off the floor",
 * so that is what is tested for (codex review of PR #3631).
 *
 * It is a MINIMUM, not an addition: a deep trough already clears the caption, so
 * ``Math.min`` leaves it exactly where it was rather than spending vertical
 * range on room that is already there. Two cases stay untouched by construction
 * — no limit (``kkLimitEur == null``: no rule is drawn, so there is nothing to
 * make room for) and nothing plotted (no span to take a fraction of).
 *
 * The extra strip is not mistakable for data: where nothing plots below the
 * limit it is empty, and ``breachSegments`` compares strictly, so no shading
 * follows the floor into it.
 *
 * **This is the AXIS bound, and only that.** The breach ``ReferenceArea`` takes
 * ``chartDataFloor`` instead, because the red band is a statement about money
 * rather than a presentation device: `liquiditaetsplanung.md` §"Danger shading"
 * asks for "the area between the curve and the limit line […] only below the
 * limit, not full height", so shading down to a padded floor would draw a €1
 * breach as a €15.000 red block (second codex round on PR #3631 — an earlier
 * draft of this fix did exactly that, reasoning from #1509's comment instead of
 * the requirement). Where the clearance does not bind the two floors are the
 * same value, which is every case that existed before HOF-16.
 *
 * The cost is that the curve gets ``1 / (1 + f)`` of the vertical range instead
 * of all of it.
 */
export function chartYFloor(rows: ChartFloorRow[], kkLimitEur: number | null): number {
  const floor = chartDataFloor(rows, kkLimitEur);
  if (kkLimitEur == null) return floor;

  let dataMax = -Infinity;
  for (const r of rows) {
    if (r.actualEur != null && r.actualEur > dataMax) dataMax = r.actualEur;
    if (r.planEur != null && r.planEur > dataMax) dataMax = r.planEur;
  }
  if (dataMax === -Infinity) return floor; // nothing plotted — no span to take a fraction of
  // Nor when the limit is above EVERYTHING plotted, which zoom/pan can produce
  // by slicing to a window that is entirely in breach (#2436). The YAxis top is
  // ``dataMax``, so the rule is then outside the domain and recharts discards
  // it: there is no rule on screen to make room for, and clearance would only
  // flatten the curve — 1 € of real variation stretched over a 5.000 € domain.
  if (kkLimitEur > dataMax) return floor;

  // Give the rule room to be a line rather than the axis. The reference span, in
  // falling order of what is actually on screen: the plotted height above the
  // limit; failing that (a curve peaking exactly ON the limit) the limit's own
  // magnitude; failing that (a limit of 0) one euro — arbitrary but positive,
  // and only reachable when the limit and every plotted value are 0.
  let reference = dataMax - kkLimitEur;
  if (reference <= 0) reference = Math.abs(kkLimitEur);
  if (reference <= 0) reference = 1;
  return Math.min(floor, kkLimitEur - reference * KK_LIMIT_FLOOR_CLEARANCE);
}

/**
 * HOF-47 — how far above the plotted data the KK-limit may sit and still be
 * pulled into view, as a multiple of the plotted span.
 *
 * In data units rather than pixels on purpose: the alternative — deriving the
 * cap from the plot's rendered height — is more faithful and would drag layout
 * into a function that is unit-tested without a DOM, which is the single
 * property that makes ``chartYFloor`` testable at all.
 *
 * At the cap the domain is ``span + gap + clearance`` = ``span × 4,4``, so the
 * plotted variation still occupies ≈¼ of the plot height. Beyond it the curve
 * would flatten toward a straight line and the rule is annotated instead — that
 * is the case the third codex round on PR #3631 was describing ("1 € of
 * variation stretched over a 5.000 € domain"), and it is excluded here rather
 * than argued with. Set to ``Infinity`` to always extend, ``0`` to always
 * annotate; that one constant is the whole reversal of this decision.
 */
const KK_LIMIT_CEIL_MAX_GAP = 3;

/**
 * The room the y-ceiling leaves ABOVE the KK-limit when it extends to hold it,
 * as a fraction of the span between the limit and the data floor.
 *
 * Symmetric to ``KK_LIMIT_FLOOR_CLEARANCE`` and for the same reason: without it
 * the rule is drawn exactly ON the plot's top edge — "the rule is the axis, not
 * a line", the defect HOF-16 named at the other bound. Proportional, so it is
 * the same share of the plot height at any farm's magnitudes (a euro pad would
 * be invisible on a Mio.-€ chart and swallow a small one).
 */
const KK_LIMIT_CEIL_CLEARANCE = 0.1;

/**
 * The plotted top — ``max`` over the series actually plotted, the mirror of
 * ``chartDataFloor``, and what recharts' ``'dataMax'`` resolves to for this
 * chart's two ``<Line>``s.
 *
 * Unlike ``chartDataFloor`` it does NOT fall back to the limit when nothing is
 * plotted: the limit is the very thing this value is compared against, so a
 * fallback to it would make every empty window read as "the limit is exactly at
 * the top". ``null`` says "no plotted top", and every caller branches on it.
 */
export function chartDataCeil(rows: ChartFloorRow[]): number | null {
  let dataMax = -Infinity;
  for (const r of rows) {
    if (r.actualEur != null && r.actualEur > dataMax) dataMax = r.actualEur;
    if (r.planEur != null && r.planEur > dataMax) dataMax = r.planEur;
  }
  return dataMax === -Infinity ? null : dataMax;
}

/** The plotted bottom, with NO limit anchor — the raw counterpart of
 *  ``chartDataCeil``. Deliberately not ``chartDataFloor(rows, null)``: that one
 *  anchors on 0, so on an all-positive window it would report 0 and inflate the
 *  span the off-scale ratio is measured against. */
function plottedMin(rows: ChartFloorRow[]): number | null {
  let dataMin = Infinity;
  for (const r of rows) {
    if (r.actualEur != null && r.actualEur < dataMin) dataMin = r.actualEur;
    if (r.planEur != null && r.planEur < dataMin) dataMin = r.planEur;
  }
  return dataMin === Infinity ? null : dataMin;
}

/**
 * HOF-47 — is the KK-limit so far above everything plotted that holding it in
 * view would cost more than it buys?
 *
 * True only when the limit is above the plotted top (``gap > 0``), there IS a
 * plotted span to lose (``span > 0``), and the gap exceeds
 * ``KK_LIMIT_CEIL_MAX_GAP`` multiples of that span. Both guards matter:
 *
 * - ``gap > 0`` — below that the rule is already inside the domain and recharts
 *   draws it. ``isInRange`` is inclusive, so a limit sitting exactly ON the
 *   plotted top still renders and is not off-scale.
 * - ``span > 0`` — a flat window has no curve resolution to trade away, so
 *   extending is strictly better than annotating however deep the breach is.
 *
 * The ratio is what makes this scale-invariant, and what makes it agree with
 * codex's #3631 objection where that objection holds while still fixing the case
 * it was raised on: at −50.001 / −50.002 against a −50.000 limit the gap is €1
 * against a span of €1, so extending costs a euro of domain and puts the rule
 * back. The pathology needs ``gap ≫ span``, and that is exactly what this cuts.
 */
export function kkLimitOffScale(rows: ChartFloorRow[], kkLimitEur: number | null): boolean {
  if (kkLimitEur == null) return false;
  const dataMax = chartDataCeil(rows);
  if (dataMax == null) return false;
  const gap = kkLimitEur - dataMax;
  if (gap <= 0) return false;
  const span = dataMax - (plottedMin(rows) as number); // non-null: dataMax != null
  if (span <= 0) return false;
  return gap > KK_LIMIT_CEIL_MAX_GAP * span;
}

/**
 * The chart's upper y-bound (HOF-47) — the mirror of ``chartYFloor``, and the
 * bound whose absence WAS the defect.
 *
 * The floor has been a computed function since #1509, anchored on the KK-limit
 * precisely so the rule the farmer is judged against is always on screen. The
 * ceiling was the bare string ``'dataMax'``. So whenever every plotted value in
 * the visible window sat below the limit, the rule was strictly outside the
 * domain, recharts' default ``ifOverflow: 'discard'`` returned ``null``, and the
 * red dashed line was ABSENT — not clipped, not faded — from the chart of the
 * farm most in need of it, with nothing on screen saying so. Two reachable
 * paths: a tenant overdrawn across the whole fixed window, and — for ANY
 * breaching tenant — zooming into the dip, since #2436 slices the rows into
 * ``visible`` and the domain is computed from ``visible``. Zooming into a breach
 * to inspect it removed the line you were inspecting it against.
 *
 * Returns ``'dataMax'`` — today's value, bit-identical — on every chart where
 * the limit is at or below the plotted top, which is every chart that is not
 * wholly in breach, i.e. nearly all of them. It extends ONLY in the branch that
 * was broken, and only while ``kkLimitOffScale`` says the limit is near enough
 * to the data to be worth holding; beyond that the domain is left alone and
 * ``BalanceChart`` says the limit is off-scale in words instead (the alternative
 * — faking a red stroke at the top edge — would draw a line where the limit is
 * not).
 *
 * The clearance is taken over ``kkLimit − chartDataFloor``, i.e. gap + span, so
 * the rule lands ``1 / (1 + 1/clearance)`` of the plot height below the top edge
 * whatever the farm's magnitudes.
 *
 * **Non-inversion, in every branch.** Where it extends, ``kkLimit >
 * chartDataFloor`` by construction (the limit is above everything plotted), so
 * the clearance term is strictly positive and the ceiling is strictly above both
 * the limit and ``chartYFloor`` — which in this branch returns the data floor
 * untouched, since the floor deliberately adds no clearance when the limit is
 * above everything plotted (that is HOF-16's own condition, and this change does
 * not touch it).
 */
export function chartYCeil(rows: ChartFloorRow[], kkLimitEur: number | null): number | 'dataMax' {
  if (kkLimitEur == null) return 'dataMax';
  const dataMax = chartDataCeil(rows);
  if (dataMax == null) return 'dataMax';
  if (kkLimitEur <= dataMax) return 'dataMax';
  if (kkLimitOffScale(rows, kkLimitEur)) return 'dataMax';
  return kkLimitEur + KK_LIMIT_CEIL_CLEARANCE * (kkLimitEur - chartDataFloor(rows, kkLimitEur));
}

/**
 * The chart's lower bound as the DATA puts it — ``min(dataMin, kkLimit)``, with
 * no clearance added. This is `chartYFloor` exactly as it stood before HOF-16.
 *
 * It is what the breach ``ReferenceArea`` uses for its ``y2``, so the red band
 * keeps meaning "down to the deepest plotted point" (#1509) rather than "down to
 * wherever the axis happens to sit". Splitting the two is what lets HOF-16 move
 * the axis for the caption's sake without overstating a breach — see
 * ``chartYFloor`` above.
 */
export function chartDataFloor(rows: ChartFloorRow[], kkLimitEur: number | null): number {
  let dataMin = Infinity;
  for (const r of rows) {
    if (r.actualEur != null && r.actualEur < dataMin) dataMin = r.actualEur;
    if (r.planEur != null && r.planEur < dataMin) dataMin = r.planEur;
  }
  const anchor = kkLimitEur ?? 0;
  if (dataMin === Infinity) return anchor;
  return Math.min(dataMin, anchor);
}

/** The selected-month highlight band's x-range, or null when the selected
 *  month is unset or outside the fixed window. */
export function monthBandRange(
  selectedYm: string | undefined,
  todayYm: string,
): { x1: string; x2: string } | null {
  if (!selectedYm) return null;
  return ymRangeBandRange(selectedYm, selectedYm, todayYm);
}

/**
 * The highlight band for an inclusive ``[startYm, endYm]`` month range (#2254 —
 * a selected Wirtschaftsjahr), CLAMPED to the fixed ±12-month chart window, or
 * null when the range lies entirely outside it. A single-month range degrades
 * to exactly ``monthBandRange`` (which delegates here).
 */
export function ymRangeBandRange(
  startYm: string,
  endYm: string,
  todayYm: string,
): { x1: string; x2: string } | null {
  const lo = windowFirstYm(todayYm);
  const hi = windowLastYm(todayYm);
  const s = startYm < lo ? lo : startYm;
  const e = endYm > hi ? hi : endYm;
  if (s > e) return null;
  return { x1: firstDayOfMonth(s), x2: lastDayOfMonth(e) };
}

/**
 * Clamp a chart band ``{x1,x2}`` (ISO dates) to the currently VISIBLE date range
 * ``[min,max]`` (#2436 — the zoom/pan row-slice). recharts positions a
 * ``ReferenceArea`` on the categorical axis by matching its endpoints to visible
 * categories, so a band whose edges fall outside the slice must be trimmed to the
 * slice edges (which ARE visible categories); returns ``null`` when the band lies
 * entirely outside the window or when nothing is visible. Also serves the
 * selected-transaction marker: a point is inside iff ``min ≤ date ≤ max``.
 */
export function clampBandToRange(
  band: { x1: string; x2: string } | null,
  min: string | undefined,
  max: string | undefined,
): { x1: string; x2: string } | null {
  if (!band || !min || !max) return null;
  if (band.x2 < min || band.x1 > max) return null;
  const x1 = band.x1 < min ? min : band.x1;
  const x2 = band.x2 > max ? max : band.x2;
  return x1 > x2 ? null : { x1, x2 };
}

/**
 * #3158 — the Säulen counterpart of ``clampBandToRange``: recharts places a
 * ``ReferenceArea`` on a categorical axis only when its endpoints are visible
 * categories, and at MONTH grain a membership check IS the clamp (a band cannot
 * be trimmed *into* a month). Returns the selected month when it is one of the
 * visible columns, else ``null`` — off-slice, outside the fixed window, or no
 * month selection at all (a Wirtschaftsjahr selection passes ``undefined``).
 */
export function columnBandYm(
  selectedYm: string | undefined,
  visibleYms: readonly string[],
): string | null {
  if (!selectedYm) return null;
  return visibleYms.includes(selectedYm) ? selectedYm : null;
}
